import { FC, useEffect, useRef, useState } from 'react'

type Props = {
    value: number
    duration?: number
    decimals?: number
    className?: string
}

/**
 * Lightweight count-up animation for dashboard metrics.
 * Animates from the previously displayed value to the new value with easeOutCubic.
 */
export const CountUp: FC<Props> = ({ value, duration = 900, decimals = 0, className }) => {
    const [display, setDisplay] = useState(0)
    const displayRef = useRef(0)
    const rafRef = useRef<number | undefined>(undefined)

    useEffect(() => {
        const from = displayRef.current
        const start = performance.now()

        const tick = (nowTs: number) => {
            const progress = Math.min((nowTs - start) / duration, 1)
            const eased = 1 - Math.pow(1 - progress, 3)
            const current = from + (value - from) * eased
            displayRef.current = current
            setDisplay(current)
            if (progress < 1) {
                rafRef.current = requestAnimationFrame(tick)
            }
        }

        rafRef.current = requestAnimationFrame(tick)
        return () => {
            if (rafRef.current !== undefined) cancelAnimationFrame(rafRef.current)
        }
    }, [value, duration])

    const text = decimals > 0 ? display.toFixed(decimals) : Math.round(display).toLocaleString()
    return <span className={className}>{text}</span>
}
